import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useDatabase } from '../context/DatabaseContext';

const { FiLayers, FiArrowRight, FiFilter, FiInfo, FiDroplet, FiThermometer } = FiIcons;

const Seals = () => {
  const { products } = useDatabase();
  const [selectedMaterial, setSelectedMaterial] = useState('all');

  // Filter nur Dichtungen (EC0023xx)
  const sealProducts = products.filter(p => p.etim_class_code.startsWith('EC0023'));

  const materials = {
    'all': 'Alle Materialien',
    'NBR': 'NBR (Nitril)',
    'FKM': 'FKM (Fluor)',
    'EPDM': 'EPDM',
    'VMQ': 'VMQ (Silikon)'
  };

  const filteredProducts = selectedMaterial === 'all' 
    ? sealProducts 
    : sealProducts.filter(p => p.specifications.material === selectedMaterial);

  const getMaterialProperties = (material) => {
    const properties = {
      'NBR': {
        tempRange: '-40°C bis +100°C',
        applications: 'Hydraulik, Pneumatik, Mineralöle',
        advantages: ['Ölbeständig', 'Kostengünstig', 'Vielseitig'],
        color: 'bg-blue-50 text-blue-700'
      },
      'FKM': {
        tempRange: '-20°C bis +200°C',
        applications: 'Chemie, Hochtemperatur, aggressive Medien',
        advantages: ['Chemikalienbeständig', 'Hochtemperatur', 'Langlebig'],
        color: 'bg-red-50 text-red-700'
      },
      'EPDM': {
        tempRange: '-45°C bis +150°C',
        applications: 'Heißwasser, Dampf, Witterung',
        advantages: ['Witterungsbeständig', 'Ozonresistent', 'Heißwasser'],
        color: 'bg-green-50 text-green-700'
      },
      'VMQ': {
        tempRange: '-60°C bis +200°C',
        applications: 'Lebensmittel, Medizin, extreme Temperaturen',
        advantages: ['Lebensmittelecht', 'Temperaturstabil', 'Physiologisch unbedenklich'],
        color: 'bg-purple-50 text-purple-700'
      }
    };
    return properties[material];
  };

  const sealTypes = [
    {
      code: 'EC002306',
      name: 'Radialwellendichtringe',
      description: 'Dynamische Abdichtung rotierender Wellen gegen Öle und Fette',
      applications: ['Getriebe', 'Motoren', 'Pumpen', 'Kompressoren']
    },
    {
      code: 'EC002307',
      name: 'O-Ringe',
      description: 'Statische und dynamische Abdichtung in Nuten und Flanschverbindungen',
      applications: ['Hydraulik', 'Pneumatik', 'Flansche', 'Zylinder']
    }
  ];

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Dichtungstechnik</h1>
            <p className="text-gray-600 mt-2">
              Professionelle Abdichtungslösungen für industrielle Anwendungen
            </p>
          </div>
          <div className="flex items-center space-x-2 bg-red-50 text-red-700 px-4 py-2 rounded-lg border border-red-100">
            <SafeIcon icon={FiLayers} className="w-5 h-5" />
            <span className="font-medium">{sealProducts.length} Dichtungen</span>
          </div>
        </div>

        {/* Seal Types Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {sealTypes.map((type, index) => (
            <motion.div
              key={type.code}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
              initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center">
                  <SafeIcon icon={FiLayers} className="w-6 h-6 text-red-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{type.name}</h3>
                  <p className="text-gray-600 text-sm mb-3">{type.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {type.applications.map((app, i) => (
                      <span
                        key={i}
                        className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs"
                      >
                        {app}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Material Filter */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center space-x-2 mb-4">
            <SafeIcon icon={FiFilter} className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Werkstoffe</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
            {Object.entries(materials).map(([code, name]) => (
              <button
                key={code}
                onClick={() => setSelectedMaterial(code)}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  selectedMaterial === code
                    ? 'bg-red-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {name}
              </button>
            ))}
          </div>
          
          {/* Material Properties */}
          {selectedMaterial !== 'all' && getMaterialProperties(selectedMaterial) && (
            <div className={`p-4 rounded-lg ${getMaterialProperties(selectedMaterial).color}`}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <h4 className="font-semibold mb-1">Temperaturbereich</h4>
                  <p className="text-sm">{getMaterialProperties(selectedMaterial).tempRange}</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-1">Hauptanwendungen</h4>
                  <p className="text-sm">{getMaterialProperties(selectedMaterial).applications}</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-1">Vorteile</h4>
                  <div className="flex flex-wrap gap-1">
                    {getMaterialProperties(selectedMaterial).advantages.map((advantage, i) => (
                      <span key={i} className="text-xs bg-white/50 px-2 py-1 rounded">
                        {advantage}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProducts.map((product, index) => (
            <motion.div
              key={product.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                  <SafeIcon icon={FiLayers} className="w-6 h-6 text-gray-400" />
                </div>
                <div className="text-right">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    product.availability === 'In Stock' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {product.availability === 'In Stock' ? 'Verfügbar' : 'Nicht verfügbar'}
                  </span>
                  {product.specifications.material && (
                    <div className="mt-1">
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                        {product.specifications.material}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <h3 className="font-semibold text-gray-900 mb-1">
                {product.manufacturer_sku}
              </h3>
              <p className="text-sm text-gray-600 mb-3">{product.manufacturer}</p>

              <div className="space-y-2 mb-4">
                {product.specifications.inner_diameter_mm && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Innen-Ø:</span>
                    <span className="font-medium">{product.specifications.inner_diameter_mm} mm</span>
                  </div>
                )}
                {product.specifications.outer_diameter_mm && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Außen-Ø:</span>
                    <span className="font-medium">{product.specifications.outer_diameter_mm} mm</span>
                  </div>
                )}
                {product.specifications.width_mm && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Breite:</span>
                    <span className="font-medium">{product.specifications.width_mm} mm</span>
                  </div>
                )}
                {product.specifications.temperature_range && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 flex items-center">
                      <SafeIcon icon={FiThermometer} className="w-3 h-3 mr-1" />
                      Temp.:
                    </span>
                    <span className="font-medium text-xs">
                      {product.specifications.temperature_range.min_celsius}°C bis {product.specifications.temperature_range.max_celsius}°C
                    </span>
                  </div>
                )}
              </div>

              <Link
                to={`/product/${product.id}`}
                className="flex items-center justify-between w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
              >
                <span>Details anzeigen</span>
                <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
              </Link>
            </motion.div>
          ))}
        </div>

        {/* Compatibility Matrix */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center space-x-3 mb-4">
            <SafeIcon icon={FiDroplet} className="w-5 h-5 text-blue-600" />
            <h2 className="text-xl font-bold text-gray-900">Medienbeständigkeit</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 font-semibold text-gray-900">Medium</th>
                  <th className="text-center py-2 font-semibold text-gray-900">NBR</th>
                  <th className="text-center py-2 font-semibold text-gray-900">FKM</th>
                  <th className="text-center py-2 font-semibold text-gray-900">EPDM</th>
                  <th className="text-center py-2 font-semibold text-gray-900">VMQ</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <tr>
                  <td className="py-2 text-gray-900">Mineralöle</td>
                  <td className="text-center py-2"><span className="text-green-600 font-bold">++</span></td>
                  <td className="text-center py-2"><span className="text-green-600 font-bold">++</span></td>
                  <td className="text-center py-2"><span className="text-red-600 font-bold">--</span></td>
                  <td className="text-center py-2"><span className="text-yellow-600 font-bold">o</span></td>
                </tr>
                <tr>
                  <td className="py-2 text-gray-900">Wasser</td>
                  <td className="text-center py-2"><span className="text-green-600 font-bold">++</span></td>
                  <td className="text-center py-2"><span className="text-blue-600 font-bold">+</span></td>
                  <td className="text-center py-2"><span className="text-green-600 font-bold">++</span></td>
                  <td className="text-center py-2"><span className="text-blue-600 font-bold">+</span></td>
                </tr>
                <tr>
                  <td className="py-2 text-gray-900">Säuren (stark)</td>
                  <td className="text-center py-2"><span className="text-yellow-600 font-bold">o</span></td>
                  <td className="text-center py-2"><span className="text-green-600 font-bold">++</span></td>
                  <td className="text-center py-2"><span className="text-blue-600 font-bold">+</span></td>
                  <td className="text-center py-2"><span className="text-blue-600 font-bold">+</span></td>
                </tr>
                <tr>
                  <td className="py-2 text-gray-900">Ozon/UV</td>
                  <td className="text-center py-2"><span className="text-red-600 font-bold">--</span></td>
                  <td className="text-center py-2"><span className="text-green-600 font-bold">++</span></td>
                  <td className="text-center py-2"><span className="text-green-600 font-bold">++</span></td>
                  <td className="text-center py-2"><span className="text-green-600 font-bold">++</span></td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="mt-4 text-xs text-gray-600">
            <p><strong>Legende:</strong> ++ Ausgezeichnet | + Gut | o Bedingt | -- Ungeeignet</p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Seals;